import React, { useState, useEffect } from 'react';
import '../styles/analytics-responsive.css';
import { adminShortCategoryName, formatYAxisNumber } from '../utils';

import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
  BarChart, Bar,
} from 'recharts';

const defaultHealthData = {
  statusBreakdown: { open: '43.3', awarded: '36.7', closed: '15.2', cancelled: '4.8' },
  quickStats: { avgTenderValue: '₹1.59 Cr', avgTimeToAward: '34 days', mseExemption: '18.4%', startupEligible: '12.7%' }
};

const StatCard = ({ label, value, sub, change, changeColor }) => {
  const isPositive = changeColor === '#27ae60';
  return (
    <div className="analytics-stat-card" style={{
      background: '#fff',
      border: '1px solid #e8edf2',
      borderRadius: 8,
      padding: '14px 16px',
      flex: 1,
      minWidth: 0,
    }}>
      <div className="stat-label" style={{ fontSize: 14, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, whiteSpace: 'nowrap', marginBottom: 4 }}>{label}</div>
      <div className="stat-value" style={{ fontSize: 26, fontWeight: 730, color: '#1a1a2e', lineHeight: 1.1, marginBottom: 2 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#aaa', fontWeight: 500, marginBottom: 4 }}>{sub}</div>
      {change && <div style={{ fontSize: 12, color: changeColor || '#27ae60', fontWeight: 700 }}>{isPositive ? '▲' : '▼'} {change}</div>}
    </div>
  );
};

const getTotal = (data) => data.reduce((sum, d) => sum + d.value, 0);

const RADIAN = Math.PI / 180;

const buildPieLabelLayout = (data, cx, cy, outerRadius) => {
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  const labelRadius = outerRadius + 28;
  const bendOffset = 12;
  const minGap = 15;

  let cumulative = 0;
  const items = data.map((d) => {
    const startAngle = (cumulative / total) * 360;
    cumulative += d.value;
    const endAngle = (cumulative / total) * 360;
    const midAngle = (startAngle + endAngle) / 2;
    const percent = d.value / total;

    const edgeX = cx + outerRadius * Math.cos(-midAngle * RADIAN);
    const edgeY = cy + outerRadius * Math.sin(-midAngle * RADIAN);
    const rawY = cy + labelRadius * Math.sin(-midAngle * RADIAN);
    const side = Math.cos(-midAngle * RADIAN) >= 0 ? 'right' : 'left';

    return { name: d.name, color: d.color, percent, edgeX, edgeY, rawY, side };
  });

  ['left', 'right'].forEach((side) => {
    const group = items
      .map((item, i) => ({ ...item, i }))
      .filter((item) => item.side === side)
      .sort((a, b) => a.rawY - b.rawY);

    for (let k = 1; k < group.length; k++) {
      if (group[k].rawY - group[k - 1].rawY < minGap) {
        group[k].rawY = group[k - 1].rawY + minGap;
      }
    }
    group.forEach((item) => { items[item.i].finalY = item.rawY; });
  });

  items.forEach((item) => {
    if (item.finalY === undefined) item.finalY = item.rawY;
    item.finalX = item.side === 'right' ? cx + labelRadius : cx - labelRadius;
    item.bendX = item.side === 'right' ? cx + outerRadius + bendOffset : cx - outerRadius - bendOffset;
  });

  return items;
};

// Reusable "No Data" component
const NoDataMessage = ({ message = "No data available" }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    color: '#aaa',
    fontSize: 14,
    fontWeight: 500,
    minHeight: 160,
  }}>
    {message}
  </div>
);

const AdminAnalytics = () => {
  const [activeFilter, setActiveFilter] = useState('1yr');
  const [kpiData, setKpiData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [keywordData, setKeywordData] = useState([]);
  const [stateData, setStateData] = useState([]);
  const [orgData, setOrgData] = useState([]);
  const [healthData, setHealthData] = useState(defaultHealthData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filters = ['1mo', '3mo', '6mo', '1yr'];

  const renderCustomLabel = (props) => {
    const { cx, cy, outerRadius, index, percent } = props;
    if (percent < 0.005) return null;

    const layout = buildPieLabelLayout(categoryData, cx, cy, outerRadius);
    const pos = layout[index];
    if (!pos) return null;

    const shortName = adminShortCategoryName(pos.name);
    const anchor = pos.side === 'right' ? 'start' : 'end';
    const textX = pos.finalX + (pos.side === 'right' ? 6 : -6);

    return (
      <g>
        <polyline
          points={`${pos.edgeX},${pos.edgeY} ${pos.bendX},${pos.finalY} ${pos.finalX},${pos.finalY}`}
          fill="none"
          stroke="#c7c7c7"
          strokeWidth={1}
        />
        <circle cx={pos.finalX} cy={pos.finalY} r={2} fill={pos.color || '#999'} />
        <text x={textX} y={pos.finalY} textAnchor={anchor} dominantBaseline="central" style={{ fontSize: 15 }}>
          <tspan fill="#555">{shortName}</tspan>
          <tspan fill="#1a1a2e" fontWeight={700}>{`  ${(pos.percent * 100).toFixed(0)}%`}</tspan>
        </text>
      </g>
    );
  };

  useEffect(() => {
    const fetchKpis = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const API = process.env.REACT_APP_BASE_API_URL;
        const res = await fetch(`${API}/api/admin-analytics/kpis?filter=${activeFilter}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();

        if (data.success) {
          setKpiData(data.kpiData);
          if (data.chartData) {
            setChartData(data.chartData);
          }
          if (data.pieChartData) setCategoryData(data.pieChartData);
          if (data.keywordData) setKeywordData(data.keywordData);
          if (data.stateData) setStateData(data.stateData);
          if (data.orgData) setOrgData(data.orgData);
          if (data.healthData) setHealthData(data.healthData);
        } else {
          setError(data.message || 'Failed to load data');
        }
      } catch (err) {
        console.error('Failed to fetch KPI data', err);
        setError('Network error occurred');
      } finally {
        setLoading(false);
      }
    };
    fetchKpis();
  }, [activeFilter]);

  return (
    <div className="analytics-root" style={{
      minWidth: 1024,
      fontFamily: "'Segoe UI', sans-serif",
      color: '#1a1a2e',
      padding: '0 4px'
    }}>

      <style>{`
        .recharts-wrapper svg,
        .recharts-wrapper svg * {
          outline: none !important;
        }
      `}</style>

      <div className="analytics-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h4 style={{ fontWeight: 700, fontSize: 20, marginBottom: 2, color: '#1a1a2e' }}>Admin Analytics Dashboard</h4>
          <p style={{ fontSize: 12, color: '#888', margin: 0 }}>Platform-wide tender intelligence at a glance</p>
        </div>
        <div className="analytics-filters" style={{ display: 'flex', gap: 8 }}>
          {filters.map(f => (
            <button
              key={f}
              onClick={() => {
                if (activeFilter !== f) {
                  setLoading(true);
                  setActiveFilter(f);
                }
              }}
              style={{
                padding: '6px 18px',
                border: activeFilter === f ? '2px solid #00897b' : '1.5px solid #e0e0e0',
                borderRadius: 50,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                background: activeFilter === f ? '#e0f5f3' : '#fff',
                color: activeFilter === f ? '#00897b' : '#888',
                transition: 'all 0.2s',
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="analytics-stats-row" style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
        {error ? (
          <div style={{ padding: '20px', color: '#e74c3c', fontSize: 14, fontWeight: 'bold' }}>Error: {error}</div>
        ) : loading || !kpiData ? (
          <div style={{ padding: '20px', color: '#888', fontSize: 14 }}>Loading KPIs...</div>
        ) : (
          <>
            <StatCard label="Total Tenders Listed" value={kpiData.tendersListed.value} sub="This period" change={`${Math.abs(kpiData.tendersListed.change)}% vs last period`} changeColor={kpiData.tendersListed.change >= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Active Opportunities" value={kpiData.activeOpportunities.value} sub="Open tenders" change={`${Math.abs(kpiData.activeOpportunities.change)}% vs last period`} changeColor={kpiData.activeOpportunities.change >= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Awarded Tenders" value="N/A" sub="Data currently unavailable" change="" />
            <StatCard label="Total Platform Worth" value={kpiData.platformWorth.value} sub="Aggregate value" change={`${Math.abs(kpiData.platformWorth.change)}% vs last period`} changeColor={kpiData.platformWorth.change >= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Registered Customers" value={kpiData.registeredCustomers.value} sub="Active users" change={`${Math.abs(kpiData.registeredCustomers.change)}% growth`} changeColor={kpiData.registeredCustomers.change >= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Avg Win Rate" value="N/A" sub="Data currently unavailable" change="" />
          </>
        )}
      </div>

      <div className="analytics-chart-row" style={{ display: 'flex', gap: 14, marginBottom: 18 }}>

        <div className="analytics-chart-panel" style={{ flex: '1 1 58%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>
            {activeFilter === '1yr' ? 'Yearly Tender Activity' : activeFilter === '1mo' ? 'Daily Tender Activity' : 'Monthly Tender Activity'}
          </div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 12 }}>Listings and opportunities over time</div>
          <div style={{ flex: 1, minHeight: 320 }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
            ) : chartData.length === 0 ? (
              <NoDataMessage />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 35, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorListed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1565C0" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#1565C0" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="colorOpp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#27ae60" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#27ae60" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ dy: 10, fontSize: 16, fill: '#999' }} axisLine={false} tickLine={false} interval={activeFilter === '1mo' ? 2 : 'preserveStartEnd'} />
                  <YAxis tickFormatter={formatYAxisNumber} tick={{ dx: -80, fontSize: 16, fill: '#999', textAnchor: 'start' }} width={90} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 13, borderRadius: 6, border: '1px solid #e0e0e0' }} />
                  <Legend iconSize={18} wrapperStyle={{ fontSize: 14, paddingTop: 8 }} formatter={(value) => <span style={{ color: '#666' }}>{value}</span>} />
                  <Area type="monotone" dataKey="Listed" stroke="#1565c0" strokeWidth={2} fill="url(#colorListed)" dot={false} />
                  <Area type="monotone" dataKey="Opportunities" stroke="#27ae60" strokeWidth={2} fill="url(#colorOpp)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="analytics-chart-panel" style={{ flex: '1 1 38%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Tenders by Category</div>
          </div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 4 }}>Distribution of active tenders</div>
          <div style={{ flex: 1, minHeight: 230 }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
            ) : categoryData.length === 0 ? (
              <NoDataMessage />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 16, right: 65, left: 65, bottom: 16 }}>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    outerRadius={55}
                    dataKey="value"
                    labelLine={false}
                    label={renderCustomLabel}
                    isAnimationActive={false}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} stroke="#fff" strokeWidth={1} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} Tenders`, adminShortCategoryName(name)]} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {!loading && categoryData.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
              {[...categoryData]
                .sort((a, b) => b.value - a.value)
                .map(c => {
                  const total = getTotal(categoryData);
                  const pct = total ? ((c.value / total) * 100).toFixed(0) : 0;
                  return (
                    <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: c.color, flexShrink: 0 }} />
                      <span style={{ color: '#555', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{adminShortCategoryName(c.name)}</span>
                      <span style={{ color: '#333', fontWeight: 600 }}>{pct}%</span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      <div className="analytics-chart-row" style={{ display: 'flex', gap: 14, marginBottom: 18 }}>

        <div className="analytics-chart-panel" style={{ flex: '1 1 45%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>Top Departments</div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 12 }}>Top departments by volume</div>
          <div style={{ flex: 1, minHeight: 220 }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
            ) : keywordData.length === 0 ? (
              <NoDataMessage />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={keywordData} margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ dy: 10, fontSize: 16, fill: '#999' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ dx: -170, fontSize: 16, fill: '#555', textAnchor: 'start' }} width={180} axisLine={false} tickLine={false} />
                  <Tooltip cursor={false} formatter={(value, name) => [`${value} Searches`, name]} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                  <Bar dataKey="value" fill="#1a7a6e" radius={[0, 3, 3, 0]} barSize={14} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="analytics-chart-panel" style={{ flex: '1 1 50%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>State-wise Distribution</div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 12 }}>Tender count by state (top 8)</div>
          <div style={{ flex: 1, minHeight: 220 }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
            ) : stateData.length === 0 ? (
              <NoDataMessage />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stateData} margin={{ top: 0, right: 10, left: 0, bottom: 65 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="state" tick={{ dy: 10, fontSize: 13, fill: '#666' }} axisLine={false} tickLine={false} angle={-35} textAnchor="end" interval={0} />
                  <YAxis tickFormatter={formatYAxisNumber} tick={{ dx: -10, fontSize: 16, fill: '#999' }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={false} formatter={(value, name) => [`${value} Tenders`, name]} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                  <Bar dataKey="value" fill="#1565c0" radius={[3, 3, 0, 0]} barSize={22} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="analytics-chart-row" style={{ display: 'flex', gap: 14, marginBottom: 8 }}>

        <div className="analytics-chart-panel" style={{ flex: '1 1 55%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 14 }}>Top Organizations by Tender Volume</div>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12, minHeight: 160 }}>Loading...</div>
          ) : orgData.length === 0 ? (
            <NoDataMessage />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  {['ORGANIZATION', 'TENDERS', 'EST. VALUE (₹L)', 'MARKET SHARE'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px 8px', fontSize: 12, color: '#aaa', fontWeight: 700, letterSpacing: 0.3 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orgData.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f8f8f8' }}>
                    <td style={{ padding: '9px 8px', color: '#333', fontSize: 14 }}>{row.org}</td>
                    <td style={{ padding: '9px 8px', color: '#333', fontSize: 14 }}>{row.tenders}</td>
                    <td style={{ padding: '9px 8px', color: '#333', fontSize: 14 }}>{row.estValue}</td>
                    <td style={{ padding: '9px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 3, height: 6 }}>
                          <div style={{ background: '#00897b', height: '100%', borderRadius: 3, width: `${row.marketShare}%` }} />
                        </div>
                        <span style={{ fontSize: 13, color: '#555', minWidth: 32, textAlign: 'right' }}>{row.marketShare}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="analytics-chart-panel" style={{ flex: '1 1 40%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 14 }}>Platform Health Overview</div>

          <div className="analytics-health-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Open Tenders', value: healthData.statusBreakdown.open === 'N/A' ? 'N/A' : `${healthData.statusBreakdown.open}%`, color: '#27ae60' },
              { label: 'Awarded', value: healthData.statusBreakdown.awarded === 'N/A' ? 'N/A' : `${healthData.statusBreakdown.awarded}%`, color: '#00897b' },
              { label: 'Closed', value: healthData.statusBreakdown.closed === 'N/A' ? 'N/A' : `${healthData.statusBreakdown.closed}%`, color: '#1565c0' },
              { label: 'Cancelled', value: healthData.statusBreakdown.cancelled === 'N/A' ? 'N/A' : `${healthData.statusBreakdown.cancelled}%`, color: '#e74c3c' },
            ].map(m => (
              <div key={m.label} style={{ border: '1px solid #e8edf2', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 12, color: '#aaa', fontWeight: 500, marginBottom: 4 }}>{m.label}</div>
                <div style={{ fontSize: 26, fontWeight: 730, color: m.color }}>{m.value}</div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6 }}>Status breakdown</div>
            <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', gap: 1 }}>
              <div style={{ flex: healthData.statusBreakdown.open === 'N/A' ? 0 : healthData.statusBreakdown.open, background: '#27ae60' }} />
              <div style={{ flex: healthData.statusBreakdown.awarded === 'N/A' ? 0 : healthData.statusBreakdown.awarded, background: '#00897b' }} />
              <div style={{ flex: healthData.statusBreakdown.closed === 'N/A' ? 0 : healthData.statusBreakdown.closed, background: '#1565c0' }} />
              <div style={{ flex: healthData.statusBreakdown.cancelled === 'N/A' ? 0 : healthData.statusBreakdown.cancelled, background: '#e74c3c' }} />
            </div>
          </div>

          <div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: '#333' }}>Quick Stats</div>
            {[
              { label: 'Avg Tender Value', value: healthData.quickStats.avgTenderValue },
              { label: 'Avg Time to Award', value: healthData.quickStats.avgTimeToAward },
              { label: 'MSE Exemption Tenders', value: healthData.quickStats.mseExemption === 'N/A' ? 'N/A' : healthData.quickStats.mseExemption },
              { label: 'Startup Eligible Tenders', value: healthData.quickStats.startupEligible === 'N/A' ? 'N/A' : healthData.quickStats.startupEligible },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < 3 ? '1px solid #f5f5f5' : 'none' }}>
                <span style={{ fontSize: 14, color: '#666', fontWeight: 500 }}>{s.label}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};

export default AdminAnalytics;